import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, Check, CheckCircle2, ClipboardPaste, Clock, Copy, CreditCard, ExternalLink,
  FileText, Hash, Headphones, Hourglass, ImagePlus, Info, Loader2, QrCode, ShieldCheck, Trash2, XCircle,
} from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice, formatDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";

const PROOF_MAX_MB = 5;
const TYPE_LABEL = { link: "Paiement par lien", manual: "Paiement manuel", qr: "Paiement par QR Code", other: "Autre" };
const mediaSrc = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("/api/") ? fileUrl(u) : u);

const copyText = async (text, msg) => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast.success(msg || "Copié !");
  } catch (e) {
    toast.error("Copie impossible");
  }
};

function Steps({ current }) {
  const steps = ["Commande", "Paiement", "Confirmation"];
  return (
    <div className="flex items-center justify-center gap-0" data-testid="payment-steps">
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <div key={label} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <span className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-black transition-colors duration-200 ${
                done ? "bg-emerald-500 text-white" : active ? "bg-brand text-white shadow-lg shadow-brand/30" : "border-2 border-line bg-white text-slate-400"
              }`}>
                {done ? <Check className="h-4 w-4" /> : n}
              </span>
              <span className={`text-[11px] font-bold ${active ? "text-brand" : done ? "text-emerald-600" : "text-slate-400"}`}>{label}</span>
            </div>
            {n < steps.length && <span className={`mx-2 mb-5 h-0.5 w-10 sm:w-24 ${done ? "bg-emerald-400" : "bg-line"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function CopyRow({ label, value, testId, onCopy }) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="flex items-stretch gap-2">
        <div data-testid={testId} className="flex min-w-0 flex-1 items-center rounded-xl border border-line bg-surface px-3.5 py-3 font-mono text-sm font-bold text-ink">
          <span className="truncate">{value}</span>
        </div>
        <button
          data-testid={`${testId}-copy`}
          onClick={onCopy}
          className="flex shrink-0 items-center gap-1.5 rounded-xl bg-brand px-4 text-xs font-bold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95"
        >
          <Copy className="h-4 w-4" /> Copier
        </button>
      </div>
    </div>
  );
}

function InstructionsBox({ text }) {
  const lines = String(text || "").split("\n").map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return null;
  return (
    <div className="rounded-2xl border border-brand/15 bg-brand-soft p-4" data-testid="payment-instructions">
      <p className="mb-2 text-xs font-black uppercase tracking-wide text-brand">Instructions à suivre</p>
      <ol className="space-y-1.5">
        {lines.map((line, i) => (
          <li key={i} className="flex items-start gap-2 text-xs leading-snug text-slate-700">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand text-[9px] font-black text-white">{i + 1}</span>
            <span>{line.replace(/^\d+[.)]\s*/, "")}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function PaymentPage() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [order, setOrder] = useState(location.state?.order || null);
  const [email, setEmail] = useState(location.state?.email || "");
  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading] = useState(!location.state?.order);
  const [methods, setMethods] = useState([]);
  const [selected, setSelected] = useState("");
  const [txid, setTxid] = useState("");
  const [proof, setProof] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const proofRef = useRef(null);

  useSeo({ title: `Paiement — ${orderNumber} — ISMO-TECH`, noIndex: true });

  const effectiveEmail = email || (user ? user.email : "");

  const loadOrder = (withEmail) => {
    setLoading(true);
    api.get(`/orders/${orderNumber}`, { params: withEmail ? { email: withEmail } : {} })
      .then(({ data }) => setOrder(data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!order) loadOrder(email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  useEffect(() => {
    if (!order) return;
    api.get(`/orders/${orderNumber}/payment-methods`, { params: { email: effectiveEmail } })
      .then(({ data }) => setMethods(data.methods || []))
      .catch(() => setMethods([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, effectiveEmail]);

  const paid = order?.payment_status === "paid";
  const underReview = !paid && order?.status === "payment_review" && !justSubmitted;
  const rejected = !paid && order?.status === "payment_rejected";
  const claim = order?.payment_claim;
  const step = paid || justSubmitted || underReview ? 3 : 2;

  const selectedMethod = useMemo(() => methods.find((m) => m.id === selected), [methods, selected]);

  const recordSelect = (method) => {
    setSelected(method.id);
    if (effectiveEmail || user) {
      api.post(`/orders/${orderNumber}/payment-select`, { method_id: method.id, email: effectiveEmail }).catch(() => {});
    }
    setTimeout(() => {
      document.getElementById("payment-detail-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const convertedLabel = (m) => {
    if (m?.convert_enabled && Number(m?.convert_rate) > 0 && m?.convert_currency) {
      const v = (order.total || 0) / Number(m.convert_rate);
      return `${v.toLocaleString("fr-FR", { maximumFractionDigits: 4 })} ${m.convert_currency}`;
    }
    return "";
  };

  const pasteTxid = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) { setTxid(text.trim()); toast.success("Collé"); }
    } catch (e) {
      toast.error("Autorisez l'accès au presse-papiers pour coller");
    }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png", "pdf"].includes(ext)) return toast.error("Format non supporté (JPG, PNG, PDF)");
    if (file.size > PROOF_MAX_MB * 1024 * 1024) return toast.error(`Fichier trop lourd (max ${PROOF_MAX_MB} Mo)`);
    setUploadingProof(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(
        `/orders/${orderNumber}/payment-proof?email=${encodeURIComponent(effectiveEmail)}`,
        body,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setProof({ ...data, isPdf: ext === "pdf", preview: ext === "pdf" ? "" : URL.createObjectURL(file) });
      toast.success("Preuve de paiement ajoutée");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally {
      setUploadingProof(false);
      if (proofRef.current) proofRef.current.value = "";
    }
  };

  const submitClaim = async () => {
    if (!selectedMethod) return toast.error("Choisissez d'abord votre moyen de paiement");
    if (selectedMethod.require_txid && !txid.trim())
      return toast.error("Indiquez votre ID / référence de transaction");
    if (selectedMethod.require_proof && !proof?.path)
      return toast.error("Ajoutez une preuve de paiement");
    if (!effectiveEmail) return toast.error("Indiquez l'email utilisé lors de la commande");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/orders/${orderNumber}/payment-claim`, {
        method_id: selectedMethod.id,
        phone: "",
        transaction_id: txid.trim(),
        proof_path: proof?.path || "",
        email: effectiveEmail,
      });
      setOrder(data);
      setJustSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Votre demande de paiement a bien été envoyée.");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container-it flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container-it py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-ink">Commande introuvable</h1>
        <p className="mt-2 text-sm text-slate-500">Saisissez l'email utilisé lors de la commande pour continuer.</p>
        <div className="mx-auto mt-6 flex max-w-md gap-2">
          <input data-testid="payment-lookup-email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
            placeholder="votre@email.com" className="h-12 flex-1 rounded-xl border border-line px-4 text-sm" />
          <button data-testid="payment-lookup-submit" onClick={() => { setEmail(emailInput); loadOrder(emailInput); }}
            className="rounded-xl bg-brand px-5 text-sm font-bold text-white">Rechercher</button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-it py-6 lg:py-10" data-testid="payment-page">
      <Steps current={step} />

      <div className="mt-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink sm:text-3xl">
            {paid ? "Paiement confirmé" : justSubmitted || underReview ? "Paiement en cours de vérification" : "💳 Effectuez votre paiement"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {paid || justSubmitted || underReview ? `Commande ${order.order_number}` : "Suivez les étapes ci-dessous pour finaliser votre commande"}
          </p>
        </div>
        <Link to="/support" className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand">
          <Headphones className="h-3.5 w-3.5" /> Besoin d'aide ?
        </Link>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)]">
        {/* -------- Zone principale -------- */}
        <div className="space-y-5">
          {/* Montant exact */}
          {!paid && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand/20 bg-brand-soft p-5" data-testid="payment-amount-banner">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-white"><Info className="h-5 w-5" /></span>
                <div>
                  <p className="text-sm font-extrabold text-brand">Montant exact à payer</p>
                  <p className="mt-0.5 text-xs text-slate-600">Veuillez effectuer le paiement du montant exact indiqué.</p>
                </div>
              </div>
              <p className="font-display text-3xl font-black text-brand sm:text-4xl" data-testid="payment-amount">{formatPrice(order.total, "XOF")}</p>
            </div>
          )}

          {paid ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="payment-paid-banner">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white"><CheckCircle2 className="h-9 w-9" /></span>
              <h2 className="mt-4 font-display text-xl font-extrabold text-ink">🟢 Votre paiement est confirmé !</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">Votre commande est maintenant validée. Retrouvez votre livraison et votre facture dans « Mes commandes ».</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/compte/commandes" data-testid="payment-goto-orders" className="flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">
                  Suivre ma commande <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to={`/commande/${order.order_number}`} className="rounded-full border-2 border-ink px-6 py-3 text-sm font-bold text-ink hover:border-brand hover:text-brand">Voir ma commande</Link>
              </div>
            </div>
          ) : justSubmitted || underReview ? (
            <div className="rounded-2xl border border-amber-200 bg-white p-6 sm:p-8" data-testid="payment-review-banner">
              <div className="text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600"><Hourglass className="h-8 w-8" /></span>
                <h2 className="mt-4 font-display text-xl font-extrabold text-ink">🟠 Votre demande de paiement a bien été envoyée</h2>
                <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-slate-600">
                  Notre équipe va vérifier votre paiement avant de valider votre commande. Vous serez notifié dès sa confirmation. Vous pouvez suivre l'avancement dans « Mes commandes ».
                </p>
              </div>
              {claim && (
                <dl className="mx-auto mt-6 max-w-md space-y-2 rounded-xl border border-line bg-surface p-4 text-xs">
                  <div className="flex justify-between"><dt className="text-slate-500">Moyen de paiement</dt><dd className="font-bold text-ink">{claim.method_name}</dd></div>
                  {claim.transaction_id && <div className="flex justify-between"><dt className="text-slate-500">ID / TXID</dt><dd className="font-bold text-ink">{claim.transaction_id}</dd></div>}
                  <div className="flex justify-between"><dt className="text-slate-500">Déclaré le</dt><dd className="font-bold text-ink">{formatDateTime(claim.submitted_at)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Preuve de paiement</dt><dd className="font-bold text-ink">{claim.proof_path ? "Envoyée ✓" : "Non fournie"}</dd></div>
                </dl>
              )}
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to={user ? "/compte/commandes" : `/commande/${order.order_number}`} data-testid="payment-track-order" className="flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">
                  Suivre ma commande <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ) : (
            <>
              {rejected && (
                <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-5" data-testid="payment-rejected-banner">
                  <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
                  <div>
                    <p className="text-sm font-extrabold text-rose-700">🔴 Votre paiement précédent a été refusé</p>
                    {claim?.rejection_reason && <p className="mt-1 text-xs text-rose-600">Motif : {claim.rejection_reason}</p>}
                    <p className="mt-1 text-xs text-slate-600">Vérifiez vos informations puis soumettez une nouvelle confirmation ci-dessous.</p>
                  </div>
                </div>
              )}

              {/* Choix du moyen */}
              <div>
                <h2 className="font-display text-lg font-extrabold text-ink">Choisissez votre moyen de paiement</h2>
                {methods.length === 0 ? (
                  <div className="mt-4 rounded-2xl border-2 border-dashed border-line bg-white p-8 text-center" data-testid="no-payment-methods">
                    <CreditCard className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-3 text-sm font-bold text-ink">Aucun moyen de paiement disponible pour le moment</p>
                    <p className="mt-1 text-xs text-slate-500">Contactez notre <Link to="/support" className="font-bold text-brand underline">support</Link>.</p>
                  </div>
                ) : (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {methods.map((method, i) => (
                      <button
                        key={method.id}
                        data-testid={`payment-method-card-${i}`}
                        onClick={() => recordSelect(method)}
                        className={`relative flex items-center gap-3 rounded-2xl border-2 bg-white p-4 text-left transition-all duration-200 ${
                          selected === method.id ? "border-brand shadow-lg shadow-brand/10" : "border-line hover:border-brand/40"
                        }`}
                      >
                        {method.logo_url ? (
                          <img src={mediaSrc(method.logo_url)} alt={method.name} className="h-11 w-11 shrink-0 rounded-xl object-contain" />
                        ) : (
                          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-light text-brand">
                            {method.payment_type === "qr" ? <QrCode className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />}
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-extrabold text-ink">{method.name}</span>
                          <span className="block text-[11px] font-semibold text-slate-500">{TYPE_LABEL[method.payment_type] || ""}</span>
                        </span>
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${selected === method.id ? "border-brand bg-brand text-white" : "border-slate-300"}`}>
                          {selected === method.id && <Check className="h-3 w-3" />}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Panneau détaillé du moyen sélectionné */}
              {selectedMethod && (
                <div id="payment-detail-panel" className="rounded-2xl border border-line bg-white p-5 sm:p-6" data-testid="payment-detail-panel">
                  <div className="flex items-center gap-3 border-b border-line pb-4">
                    {selectedMethod.logo_url ? (
                      <img src={mediaSrc(selectedMethod.logo_url)} alt="" className="h-12 w-12 rounded-xl object-contain" />
                    ) : (
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-light text-brand">
                        {selectedMethod.payment_type === "qr" ? <QrCode className="h-6 w-6" /> : <CreditCard className="h-6 w-6" />}
                      </span>
                    )}
                    <div>
                      <p className="font-display text-lg font-extrabold text-ink">Paiement avec {selectedMethod.name}</p>
                      <span className="mt-0.5 inline-block rounded-full bg-brand-soft px-2.5 py-0.5 text-[11px] font-bold text-brand">
                        {TYPE_LABEL[selectedMethod.payment_type]}
                      </span>
                    </div>
                  </div>

                  <div className="mt-5 space-y-5">
                    {selectedMethod.description && <p className="text-sm text-slate-600">{selectedMethod.description}</p>}

                    {/* LINK */}
                    {selectedMethod.payment_type === "link" && (
                      <div className="flex flex-wrap gap-3">
                        <button
                          data-testid="pay-now-link"
                          onClick={() => { if (selectedMethod.link) window.open(selectedMethod.link, "_blank", "noopener"); else toast.info("Suivez les instructions puis confirmez."); }}
                          className="flex items-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-bold text-white hover:bg-brand/90"
                        >
                          Payer maintenant <ExternalLink className="h-4 w-4" />
                        </button>
                        {selectedMethod.link && (
                          <button onClick={() => copyText(selectedMethod.link, "Lien copié !")} data-testid="copy-link"
                            className="flex items-center gap-2 rounded-xl border border-line px-5 py-3 text-sm font-bold text-slate-600 hover:border-brand hover:text-brand">
                            <Copy className="h-4 w-4" /> Copier le lien
                          </button>
                        )}
                      </div>
                    )}

                    {/* MANUAL */}
                    {selectedMethod.payment_type === "manual" && (
                      <>
                        {selectedMethod.show_number !== false && selectedMethod.payment_number && (
                          <CopyRow label="Numéro de paiement" value={selectedMethod.payment_number} testId="manual-number"
                            onCopy={() => copyText(selectedMethod.payment_number, "Numéro copié !")} />
                        )}
                        {selectedMethod.beneficiary_name && (
                          <p className="text-sm"><span className="text-slate-500">Bénéficiaire : </span><span className="font-bold text-ink" data-testid="manual-beneficiary">{selectedMethod.beneficiary_name}</span></p>
                        )}
                      </>
                    )}

                    {/* QR */}
                    {selectedMethod.payment_type === "qr" && (
                      <>
                        {convertedLabel(selectedMethod) && (
                          <div className="rounded-xl bg-brand-soft p-3 text-center" data-testid="qr-converted">
                            <span className="text-xs text-slate-600">Montant à envoyer : </span>
                            <span className="font-display text-lg font-black text-brand">≈ {convertedLabel(selectedMethod)}</span>
                          </div>
                        )}
                        {selectedMethod.show_qr !== false && selectedMethod.qr_image_url && (
                          <div className="flex justify-center">
                            <div className="rounded-2xl border-2 border-line bg-white p-3">
                              <img data-testid="qr-image" src={mediaSrc(selectedMethod.qr_image_url)} alt="QR Code"
                                className="h-56 w-56 object-contain sm:h-64 sm:w-64" />
                            </div>
                          </div>
                        )}
                        {selectedMethod.show_address !== false && selectedMethod.address && (
                          <CopyRow label="Adresse de paiement" value={selectedMethod.address} testId="qr-address"
                            onCopy={() => copyText(selectedMethod.address, "Adresse copiée !")} />
                        )}
                        {selectedMethod.show_network !== false && selectedMethod.network && (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="qr-network">
                            <p className="text-sm font-extrabold text-amber-800">Réseau : {selectedMethod.network}</p>
                            <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-700">
                              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                              Vérifiez attentivement le réseau avant d'effectuer votre dépôt. L'utilisation d'un mauvais réseau peut entraîner la perte des fonds.
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    <InstructionsBox text={selectedMethod.instructions} />

                    {/* Confirmation */}
                    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-5">
                      <h3 className="flex items-center gap-2 text-sm font-extrabold text-ink">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Confirmez votre paiement
                      </h3>

                      {selectedMethod.require_txid && (
                        <label className="mt-4 block">
                          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                            {selectedMethod.payment_type === "qr" ? "TXID / Hash de transaction" : "ID / Référence de transaction"} <span className="text-rose-500">*</span>
                          </span>
                          <div className="flex items-stretch gap-2">
                            <div className="relative flex-1">
                              <Hash className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <input data-testid="payment-txid" value={txid} onChange={(e) => setTxid(e.target.value)}
                                placeholder={selectedMethod.payment_type === "qr" ? "Collez ici votre TXID" : "Exemple : TX123456789"}
                                className="h-12 w-full rounded-xl border border-line pl-10 pr-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10" />
                            </div>
                            <button data-testid="payment-txid-paste" onClick={pasteTxid}
                              className="flex shrink-0 items-center gap-1.5 rounded-xl border border-line px-3 text-xs font-bold text-slate-600 hover:border-brand hover:text-brand">
                              <ClipboardPaste className="h-4 w-4" /> Coller
                            </button>
                          </div>
                        </label>
                      )}

                      <div className="mt-4">
                        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          Preuve de paiement {selectedMethod.require_proof ? <span className="text-rose-500">*</span> : "— facultatif"}
                        </span>
                        <input ref={proofRef} type="file" accept=".jpg,.jpeg,.png,.pdf" data-testid="payment-proof-input"
                          onChange={(e) => uploadProof(e.target.files?.[0])} className="hidden" />
                        {proof ? (
                          <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-2.5" data-testid="payment-proof-preview">
                            {proof.isPdf ? (
                              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-white text-rose-500"><FileText className="h-5 w-5" /></span>
                            ) : (
                              <img src={proof.preview} alt="Preuve" className="h-10 w-10 rounded-md object-cover" />
                            )}
                            <span className="min-w-0 flex-1 truncate text-xs font-bold text-emerald-700">{proof.name}</span>
                            <button onClick={() => setProof(null)} data-testid="payment-proof-remove" className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        ) : (
                          <button data-testid="payment-proof-btn" onClick={() => proofRef.current?.click()} disabled={uploadingProof}
                            className="flex w-full items-center gap-2.5 rounded-xl border-2 border-dashed border-line p-4 text-left text-xs font-bold text-slate-500 hover:border-brand hover:text-brand disabled:opacity-60">
                            {uploadingProof ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                            <span>Télécharger une preuve<span className="block text-[10px] font-semibold text-slate-400">JPG, PNG ou PDF (max. {PROOF_MAX_MB} Mo)</span></span>
                          </button>
                        )}
                      </div>

                      {!user && !email && (
                        <label className="mt-4 block">
                          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Email utilisé lors de la commande <span className="text-rose-500">*</span></span>
                          <input data-testid="payment-email" type="email" value={emailInput}
                            onChange={(e) => { setEmailInput(e.target.value); setEmail(e.target.value); }}
                            placeholder="votre@email.com" className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none" />
                        </label>
                      )}

                      <button data-testid="submit-payment-btn" onClick={submitClaim} disabled={submitting}
                        className="btn-glow mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-bold text-white transition-transform duration-200 hover:scale-[1.01] active:scale-95 disabled:opacity-60">
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        {submitting ? "Envoi en cours..." : "✅ J'ai effectué le paiement"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="payment-important-note">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-xs leading-relaxed text-amber-800">
                  <span className="font-bold">Important :</span> votre commande sera validée uniquement après vérification de votre paiement par notre équipe. La licence n'est pas livrée automatiquement.
                </p>
              </div>

              <p className="flex items-center gap-2 text-[11px] text-slate-400">
                <Clock className="h-3.5 w-3.5" /> Votre commande reste réservée en attendant la vérification.
              </p>
            </>
          )}
        </div>

        {/* -------- Récapitulatif -------- */}
        <aside className="space-y-4 lg:sticky lg:top-[150px] lg:h-fit">
          <div className="rounded-2xl border border-line bg-white p-5" data-testid="payment-order-recap">
            <p className="text-[11px] font-black uppercase tracking-wide text-brand">Résumé de votre commande</p>
            <dl className="mt-4 space-y-2 text-xs">
              <div className="flex justify-between"><dt className="text-slate-500">Numéro de commande</dt><dd className="font-bold text-ink" data-testid="payment-order-number">{order.order_number}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Date</dt><dd className="font-semibold text-ink">{formatDateTime(order.created_at)}</dd></div>
            </dl>
            <ul className="mt-4 space-y-3 border-t border-line pt-4">
              {(order.items || []).map((item) => (
                <li key={item.product_id} className="flex items-center gap-3">
                  {item.image_url ? (
                    <img src={item.image_url} alt="" className="h-11 w-11 rounded-lg border border-line bg-slate-50 object-contain p-1" />
                  ) : (
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-line bg-slate-50 text-[10px] font-bold text-slate-400">IT</span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs font-bold text-ink">{item.name}</span>
                    <span className="text-[11px] text-slate-500">Quantité : {item.quantity}</span>
                  </span>
                  <span className="text-xs font-bold text-ink">{formatPrice(item.line_total, "XOF")}</span>
                </li>
              ))}
            </ul>
            <dl className="mt-4 space-y-2 border-t border-line pt-4 text-xs">
              <div className="flex justify-between"><dt className="text-slate-500">Sous-total</dt><dd className="font-semibold text-ink">{formatPrice(order.subtotal, "XOF")}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Frais de service</dt><dd className="font-semibold text-ink">{formatPrice(order.fees || 0, "XOF")}</dd></div>
              {order.discount > 0 && (
                <div className="flex justify-between"><dt className="text-slate-500">Réduction{order.promo_code ? ` (${order.promo_code})` : ""}</dt><dd className="font-semibold text-emerald-600">− {formatPrice(order.discount || 0, "XOF")}</dd></div>
              )}
              <div className="flex items-baseline justify-between border-t border-line pt-3">
                <dt className="text-sm font-black uppercase text-ink">Total à payer</dt>
                <dd className="font-display text-2xl font-black text-brand" data-testid="payment-recap-total">{formatPrice(order.total, "XOF")}</dd>
              </div>
            </dl>
            <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-brand-soft p-3.5">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <p className="text-[11px] leading-snug text-slate-600"><span className="font-bold text-brand">Paiement 100% sécurisé.</span> Le montant est calculé automatiquement et ne peut pas être modifié.</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
